import { useEffect, useRef, useState } from "react";

/* ───────────────────── Phone/SSN helpers (unchanged) ───────────────────── */

function normalizeSSN(v) {
  const digits = (v || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length >= 7) return digits.slice(0, 6) + "-" + digits.slice(6);
  return digits;
}
function normalizePhone(v) {
  const digits = (v || "").replace(/\D/g, "").slice(0, 7);
  if (digits.length === 7) return digits.slice(0, 3) + "-" + digits.slice(3);
  return digits;
}

/* ─────────────────────────── Date helpers (DMY) ─────────────────────────── */

const isoToDate = (iso) => {
  if (!iso) return null;
  const parts = iso.split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dateToIso = (date) => {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const fmtDMY = (iso) => {
  const dt = isoToDate(iso);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/* ─────────────────────── Inline DD/MM/YYYY calendar ─────────────────────── */

function MiniDatePickerDMY({ value, onChange, maxDate }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value) || null;
  const initialView = selected || new Date();
  const [view, setView] = useState(new Date(initialView.getFullYear(), initialView.getMonth(), 1));
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const s = isoToDate(value);
    if (s) setView(new Date(s.getFullYear(), s.getMonth(), 1));
  }, [value]);

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDay = (startOfMonth.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const isAfterMax = (c) => !!(maxDate && c && c.setHours(0,0,0,0) > new Date(maxDate).setHours(0,0,0,0));

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      {/* Read-only visible input in DD/MM/YYYY */}
      <input
        readOnly
        value={fmtDMY(value)}
        onClick={() => setOpen((v) => !v)}
        style={{ width: 160, cursor: "pointer" }}
        placeholder="dd/mm/áááá"
        aria-label="Veldu dagsetningu"
      />
      {/* Hidden input to help simple “required” checks if needed */}
      <input type="hidden" value={value || ""} />

      {open && (
        <div
          style={{
            position: "absolute",
            top: 38,
            left: 0,
            background: "#fff",
            border: "1px solid #d7dbe3",
            borderRadius: 8,
            padding: 8,
            zIndex: 50,
            boxShadow: "0 12px 28px rgba(0,0,0,.12)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
            <button className="btn ghost sm" type="button" onClick={prevMonth}>◀</button>
            <div style={{ fontWeight: 600 }}>
              {view.toLocaleString("is-IS", { month: "long" })} {view.getFullYear()}
            </div>
            <button className="btn ghost sm" type="button" onClick={nextMonth}>▶</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center", opacity: 0.7, fontSize: 12, marginBottom: 4 }}>
            {["Mán", "Þri", "Mið", "Fim", "Fös", "Lau", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center" }}>
            {cells.map((c, i) => {
              const active = c && selected && isSameDay(c, selected);
              const disabled = !c || isAfterMax(c);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (!c || disabled) return;
                    onChange?.(dateToIso(c)); // store ISO (YYYY-MM-DD)
                    setOpen(false);
                  }}
                  style={{
                    width: 32, height: 28,
                    borderRadius: 6, border: "1px solid #d7dbe3",
                    opacity: disabled ? 0.35 : 1,
                    background: active ? "#e0ecff" : "#fff",
                    cursor: disabled ? "default" : "pointer",
                  }}
                >
                  {c ? c.getDate() : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────── Username sanitize + validate (Icelandic friendly) ─────────
   Allowed: íslenskir stafir (á, ð, þ, æ, ö + hástafir), a–z, 0–9, punktur, undirstrik, bandstrik.
   3–30 stafir, má ekki byrja/enda á . _ -  */
const USERNAME_START_END = "A-Za-zÁÉÍÓÚÝÐÞÆÖáéíóúýðþæö0-9";
const USERNAME_RE = new RegExp(
  `^[${USERNAME_START_END}](?:[${USERNAME_START_END}._-]{1,28}[${USERNAME_START_END}])?$`
);

function sanitizeUsername(raw) {
  // NFC normalizes composed characters, then strip disallowed chars
  return String(raw || "")
    .normalize("NFC")
    .replace(new RegExp(`[^${USERNAME_START_END}._-]`, "g"), "");
}

function validateUsername(raw) {
  const s = String(raw || "");
  if (!s) return { ok: false, reason: "Vantar notendanafn" };
  if (s.length < 3 || s.length > 30) return { ok: false, reason: "Notendanafn þarf að vera 3–30 stafir" };
  if (!USERNAME_RE.test(s)) {
    return {
      ok: false,
      reason:
        "Leyfilegt: íslenskir stafir (á, ð, þ, æ, ö), a–z, 0–9, punktur, undirstrik, bandstrik (má ekki byrja/enda á sérmerki)",
    };
  }
  return { ok: true };
}

/* ─────────────────────────────── Component ─────────────────────────────── */

export default function Step1({
  data = {},
  setData = () => {},
  errors = {},
  usernameAvailability = null,
  lastCheckedUsername = "",
  checkUsername = () => {},
  clearUsernameCheck = () => {},
}) {
  const upd = (k, transform) => (eOrVal) => {
    // Supports both event and direct value (used by date picker)
    const val = typeof eOrVal === "string" ? eOrVal : eOrVal?.target?.value;
    setData((d) => ({ ...d, [k]: transform ? transform(val) : val }));
  };

  const [usernameErr, setUsernameErr] = useState("");
  const currentUsername = (data.username || "").trim();
  const showAvailability =
    lastCheckedUsername && lastCheckedUsername === currentUsername;

  return (
    <div className="grid">
      <L label="Fullt nafn*" error={errors.full_name}>
        <input autoComplete="name" value={data.full_name ?? ""} onChange={upd("full_name")} />
      </L>

      <L label="Kennitala*" error={errors.ssn}>
        <input
          inputMode="numeric"
          value={data.ssn ?? ""}
          onChange={upd("ssn", normalizeSSN)}
          placeholder="123456-7890"
        />
      </L>

      <L label="Símanúmer*" error={errors.phone_number}>
        <input
          type="tel" inputMode="numeric" autoComplete="tel"
          value={data.phone_number ?? ""}
          onChange={upd("phone_number", normalizePhone)}
          placeholder="555-5555"
        />
      </L>

      <L label="Netfang*" error={errors.email}>
        <input
          type="email"
          autoComplete="email"
          value={data.email ?? ""}
          onChange={upd("email")}
          placeholder="nafn@example.com"
        />
      </L>

      <L label="Nánasti aðstandandi*" error={errors.next_of_kin}>
        <input value={data.next_of_kin ?? ""} onChange={upd("next_of_kin")} />
      </L>

      <L label="Símanúmer aðstandanda*" error={errors.phone_number_next_of_kin}>
        <input
          type="tel" inputMode="numeric" autoComplete="tel"
          value={data.phone_number_next_of_kin ?? ""}
          onChange={upd("phone_number_next_of_kin", normalizePhone)}
          placeholder="555-5555"
        />
      </L>

      <L label="Þitt sveitarfélag*" error={errors.place}>
        <input value={data.place ?? ""} onChange={upd("place")} />
      </L>

      <L label="Hvenær byrjarðir þú í Hugarafli?*" error={errors.started_at_hugarafl}>
        {/* Store ISO in data.started_at_hugarafl, display DD/MM/YYYY */}
        <MiniDatePickerDMY
          value={data.started_at_hugarafl || ""}
          onChange={(iso) => setData((d) => ({ ...d, started_at_hugarafl: iso }))}
          maxDate={new Date()} // no future dates
        />
      </L>

      <L label="Notendanafn*" error={errors.username || usernameErr}>
        <input
          autoComplete="username"
          autoCapitalize="none"
          spellCheck="false"
          value={data.username ?? ""}
          onChange={(e) => {
            clearUsernameCheck(); // reset badge & error while typing

            const sanitized = sanitizeUsername(e.target.value);
            setData(d => ({ ...d, username: sanitized }));

            // live validate + native invalid state
            const v = validateUsername(sanitized.trim());
            setUsernameErr(v.ok ? "" : v.reason);
            e.target.setCustomValidity(v.ok ? "" : v.reason);
          }}
          onBlur={() => {
            const v = validateUsername((data.username || "").trim());
            setUsernameErr(v.ok ? "" : v.reason);
            // ensure the native state matches
            // (React reuses the same element, so we can safely query by value)
            const el = document.activeElement;
            if (el && el.tagName === "INPUT") {
              el.setCustomValidity(v.ok ? "" : v.reason);
            }
            // only check availability if the format is valid
            if (v.ok) checkUsername((data.username || "").trim());
          }}
          placeholder="t.d. jona23"
        />
        {showAvailability && usernameErr === "" && (
          usernameAvailability
            ? <small style={{ color: "#059669" }}>Laust ✅</small>
            : <small style={{ color: "#b91c1c" }}>Frátekið ❌</small>
        )}
      </L>

      <L label="Hver vísar þér? (t.d. aðstandandi/þjónusta)*" error={errors.referred_by}>
        <input value={data.referred_by ?? ""} onChange={upd("referred_by")} />
      </L>

      <L label="Kyn*" error={errors.gender}>
        <select value={data.gender ?? ""} onChange={upd("gender")}>
          <option value="">— Veldu —</option>
          <option value="Karl">Karl</option>
          <option value="Kona">Kona</option>
          <option value="Kynsegin">Kynsegin</option>
          <option value="Annað">Annað</option>
          <option value="ekkert-svar">Vil(l) ekki segja</option>
        </select>
      </L>

      <L label="Samþykki fyrir sakavottorði*" error={errors.accepts_criminal_record_check}>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={data.accepts_criminal_record_check === true}
            onChange={(e) =>
              setData((d) => ({ ...d, accepts_criminal_record_check: e.target.checked }))
            }
          />
          Ég veiti samþykki fyrir því að stjórn Hugarafls kalli eftir sakavottorði mínu ef þurfa þykir
        </label>
      </L>
    </div>
  );
}

function L({ label, error, children }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {error ? <small style={{ color: "#b91c1c" }}>{error}</small> : null}
    </label>
  );
}